import { ValueTypeSwitcher } from 'legacy/lut/components/widgets/public/partner360/common/valueTypeSwitcher/ValueTypeSwitcher'
import { selectJBPSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { Table } from 'legacy/lut/library/list'
import { FC, useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { LoaderOverlay } from 'core-components/library/loader'
import { Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'
import { getValueTypeValue } from 'partner360/widgets/gp360Administration'
import { getGlobalPartnerActualsTypes } from 'partner360/widgets/gp360Administration/helpers/getGlobalPartnerActualsTypes'

import { removePartnerFromGlobalPartner } from '../../../../../../api/removePartnersFromGlobalPartner'
import { CompletenessChart, ICompletenessChartProps } from '../../../../../completenessChart'
import {
  ExportGlobalFinReport,
  FinancialsChart,
  IFinancialsChartProps,
} from '../../../../../financialsChart'
import { IGlobalPartnerDetails } from '../../../../models/globalPartnerDetails'
import { getDataForCompletenessGlobalChart } from '../../helpers/getDataForCompletenessGlobalChart'

import { getTableColumn } from './helpers/getTableColumns'
import { ITableContentProps } from './models/tableContentProps'

import styles from './TableContent.module.scss'

export const TableContent: FC<ITableContentProps> = ({
  globalPartnerCurrentFYearTargetsNet,
  globalPartnerCurrentFYearTargetsTNet,
  globalPartnerCurrentFYearTargetsUTNet,
  globalPartnerCurrentFYearTargetsNdp,
  globalPartnerId,
  globalPartnerType,
  data,
  isLoading,
  valueType,
  onChangeValueType,
  hqPartnerId,
  fYear,
  globalPartnerName,
}) => {
  /** --------------------- Selectors --------------------- */
  const { currentFYear } = useSelector(selectJBPSettings)

  /** -------------------- Variables ----------------------- */
  const [isLoaded, setIsLoaded] = useState(false)

  const staticCrud = useMemo(() => {
    return new Crud<IGlobalPartnerDetails>({
      fetcher: new Static<IGlobalPartnerDetails>(data),
      keyField: 'EntryId',
      defaultPaging: {
        page: 0,
        pageSize: 5000,
      },
      hierarchyRoot: null,
      hierarchyField: 'ParentId',
    })
  }, [data])

  const completenessChartProps = useMemo<ICompletenessChartProps>(() => {
    const completenessChartData = getDataForCompletenessGlobalChart(data)
    return {
      countOfFullyAcknowledgedPartners: completenessChartData.filter((it) => it.IsFullyAcknowledged)
        .length,
      countOfPartnersWith100PercentCompleteness: completenessChartData.filter(
        (it) => !it.IsFullyAcknowledged && it.TotalCompleteness === 100
      ).length,
      countOfPartnerWithLess100PercentCount: completenessChartData.filter(
        (it) => it.TotalCompleteness < 100 && it.TotalCompleteness > 50 && !it.IsFullyAcknowledged
      ).length,
      countOfPartnersWithLess50PercentCompleteness: completenessChartData.filter(
        (it) => it.TotalCompleteness < 50 && !it.IsFullyAcknowledged
      ).length,
    }
  }, [data])

  const financialsChartProps = useMemo<IFinancialsChartProps>(() => {
    const rootLevel = data.filter((el) => el.ParentId === null)
    return {
      globalPartnerCurrentFYearTargets: getValueTypeValue({
        defaultValue: globalPartnerCurrentFYearTargetsNdp,
        netValue: globalPartnerCurrentFYearTargetsNet,
        tnetValue: globalPartnerCurrentFYearTargetsTNet,
        utnetValue: globalPartnerCurrentFYearTargetsUTNet,
        ndpValue: globalPartnerCurrentFYearTargetsNdp,
        valueType,
      }),
      partnersCurrentFYearActuals: rootLevel.reduce((acc, it) => {
        const finCurrentYearActuals = getValueTypeValue({
          defaultValue: it.PartnerCurrentFYearActualsNdp,
          netValue: it.PartnerCurrentFYearActualsNet,
          tnetValue: it.PartnerCurrentFYearActualsTNet,
          utnetValue: it.PartnerCurrentFYearActualsUTNet,
          ndpValue: it.PartnerCurrentFYearActualsNdp,
          valueType,
        })
        return acc + finCurrentYearActuals
      }, 0),
      partnersPrevFYearActuals: rootLevel.reduce((acc, it) => {
        const finPrevFYearActuals = getValueTypeValue({
          defaultValue: it.PartnersPrevFYearActualsNdp,
          netValue: it.PartnersPrevFYearActualsNet,
          tnetValue: it.PartnersPrevFYearActualsTNet,
          utnetValue: it.PartnersPrevFYearActualsUTNet,
          ndpValue: it.PartnersPrevFYearActualsNdp,
          valueType,
        })
        return acc + finPrevFYearActuals
      }, 0),
      partnersCurrentFYearTargets: rootLevel.reduce((acc, it) => {
        const finCurrentFYearTargets = getValueTypeValue({
          defaultValue: it.PartnersCurrentFYearTargetsNdp,
          netValue: it.PartnersCurrentFYearTargetsNet,
          tnetValue: it.PartnersCurrentFYearTargetsTNet,
          utnetValue: it.PartnersCurrentFYearTargetsUTNet,
          ndpValue: it.PartnersCurrentFYearTargetsNdp,
          valueType,
        })

        return acc + finCurrentFYearTargets
      }, 0),
      valueType,
      rollupQuotas: 0, // count rollup quotas
      fYear: currentFYear,
    }
  }, [
    data,
    globalPartnerCurrentFYearTargetsNdp,
    globalPartnerCurrentFYearTargetsNet,
    globalPartnerCurrentFYearTargetsTNet,
    globalPartnerCurrentFYearTargetsUTNet,
    currentFYear,
    valueType,
  ])

  /** -------------------- Methods ----------------------- */

  const handleRemovePartner = useCallback(
    async (item: IGlobalPartnerDetails) => {
      if (item.PartnerId) {
        setIsLoaded(true)
        await removePartnerFromGlobalPartner({
          partnerId: item.PartnerId,
          globalPartnerId,
          currentFYear,
        })
        setIsLoaded(false)
      }
    },
    [globalPartnerId, currentFYear]
  )

  const rowActions = useMemo(() => {
    if (hqPartnerId && hqPartnerId.length > 0) {
      return []
    }

    return [
      {
        id: 'remove',
        title: 'Remove',
        icon: 'kbase kbase-delete',
        action: handleRemovePartner,
        isVisible: (item: IGlobalPartnerDetails) => {
          return !!item?.IsForCalc
        },
      },
    ]
  }, [hqPartnerId, handleRemovePartner])

  return (
    <div>
      <div className={styles.content}>
        <div className={styles.tableWrapper}>
          <Table
            sourceUpdateCount={isLoading ? -1 : 1}
            hasHierarchy
            hasTranslate
            header
            rowActionsVisible="visible"
            rowActions={rowActions}
            columns={getTableColumn(currentFYear, valueType)}
            source={staticCrud}
          />
          <LoaderOverlay show={isLoaded} />
        </div>
        <div className={styles.chartWrapper}>
          <CompletenessChart {...completenessChartProps} />
          <div className={styles.currencyButtons}>
            <ValueTypeSwitcher
              valueTypes={getGlobalPartnerActualsTypes(globalPartnerType)}
              valueType={valueType}
              onChangeValueType={onChangeValueType}
            />
            <ExportGlobalFinReport
              globalPartnerId={globalPartnerId}
              fYear={fYear}
              globalPartnerName={globalPartnerName}
            />
          </div>

          <FinancialsChart {...financialsChartProps} />
        </div>
      </div>
    </div>
  )
}
